import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatError, wasteLabel, wasteColor } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { Award, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function OperatorUserPage() {
  const { userId } = useParams();
  const [subs, setSubs] = useState([]);
  const [user, setUser] = useState(null);
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    try {
      const [subsR, batchesR, users] = await Promise.all([
        api.get(`/submissions?user_id=${userId}`),
        api.get(`/tari/batches?user_id=${userId}`),
        api.get(`/admin/users`),
      ]);
      setSubs(subsR.data);
      setBatches(batchesR.data);
      setUser(users.data.find((u) => u.id === userId) || null);
      setSelected(new Set());
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId]);

  const eligible = subs.filter((s) => s.status === "approved" && !s.tari_batch_id);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(selected.size === eligible.length ? new Set() : new Set(eligible.map((s) => s.id)));

  const submitBatch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/tari/batches", { user_id: userId, submission_ids: Array.from(selected), note: note || null });
      toast.success(`Lotto TARI creato: ${data.total_kg.toFixed(2)} kg`);
      setOpen(false); setNote("");
      await reload();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const totalSel = Array.from(selected).reduce((acc, id) => {
    const s = subs.find((x) => x.id === id);
    return acc + (s?.verified_weight_kg || 0);
  }, 0);

  return (
    <Layout
      title={user ? `${user.name}` : "Utente"}
      subtitle={user ? `${user.email} · ${user.role === "business" ? "Azienda" : "Cittadino"}` : ""}
      actions={<Link to="/operator"><Button variant="ghost" className="gap-2" data-testid="back-queue-2"><ArrowLeft className="w-4 h-4" /> Coda</Button></Link>}
    >
      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Conferimenti approvati pronti</div>
          <div className="font-display text-3xl font-semibold mt-1">{eligible.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Selezionati per TARI</div>
          <div className="font-display text-3xl font-semibold mt-1">{selected.size} <span className="text-sm text-muted-foreground font-normal">/ {totalSel.toFixed(2)} kg</span></div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Lotti TARI creati</div>
          <div className="font-display text-3xl font-semibold mt-1">{batches.length}</div>
        </CardContent></Card>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg font-semibold">Conferimenti approvati (selezionabili per TARI)</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button disabled={selected.size === 0} className="rounded-full gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="open-tari-dialog">
              <Award className="w-4 h-4" /> Riconosci per TARI ({selected.size})
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Conferma riconoscimento TARI</DialogTitle></DialogHeader>
            <div className="text-sm space-y-3">
              <div>Stai per marcare <strong>{selected.size}</strong> conferimenti per un totale di <strong>{totalSel.toFixed(2)} kg</strong> come riconosciuti per lo sconto TARI.</div>
              <Textarea placeholder="Nota (opzionale)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="tari-note" />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} data-testid="tari-cancel">Annulla</Button>
              <Button onClick={submitBatch} disabled={busy} data-testid="tari-confirm" className="bg-emerald-600 hover:bg-emerald-700">Conferma</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="mb-8">
        <CardContent className="p-0">
          {eligible.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">Nessun conferimento approvato disponibile per il riconoscimento TARI.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox checked={selected.size === eligible.length && eligible.length > 0} onCheckedChange={toggleAll} data-testid="select-all" />
                  </TableHead>
                  <TableHead>Data</TableHead>
                  <TableHead>Codice</TableHead>
                  <TableHead>Tipologia</TableHead>
                  <TableHead>Peso</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {eligible.map((s) => (
                  <TableRow key={s.id} data-testid={`sel-row-${s.id}`}>
                    <TableCell><Checkbox checked={selected.has(s.id)} onCheckedChange={() => toggle(s.id)} data-testid={`sel-${s.id}`} /></TableCell>
                    <TableCell className="text-xs">{new Date(s.created_at).toLocaleDateString("it-IT")}</TableCell>
                  <TableCell className="font-mono text-xs">{s.barcode_full || "—"}</TableCell>
                    <TableCell><span className="inline-flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: wasteColor(s.waste_type) }} />{wasteLabel(s.waste_type)}</span></TableCell>
                    <TableCell className="font-medium">{s.verified_weight_kg} kg</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <h2 className="font-display text-lg font-semibold mb-4">Lotti TARI già creati</h2>
      <Card>
        <CardContent className="p-0">
          {batches.length === 0 ? (
            <div className="p-8 text-sm text-muted-foreground text-center">Nessun lotto TARI creato per questo utente.</div>
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>ID</TableHead><TableHead>N. conferimenti</TableHead><TableHead>Totale</TableHead><TableHead>Stato</TableHead></TableRow></TableHeader>
              <TableBody>
                {batches.map((b) => (
                  <TableRow key={b.id} data-testid={`batch-${b.id}`}>
                    <TableCell className="text-xs">{new Date(b.created_at).toLocaleDateString("it-IT")}</TableCell>
                    <TableCell className="font-mono text-xs">{b.id.slice(0, 8)}</TableCell>
                    <TableCell>{b.submission_ids?.length}</TableCell>
                    <TableCell className="font-medium">{b.total_kg?.toFixed(2)} kg</TableCell>
                    <TableCell><StatusBadge status={b.status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <h2 className="font-display text-lg font-semibold mb-4 mt-8">Tutti i conferimenti</h2>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Codice</TableHead><TableHead>Tipologia</TableHead><TableHead>Peso</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {subs.map((s) => (
                <TableRow key={s.id} className="hover:bg-gray-50">
                  <TableCell className="text-xs">{new Date(s.created_at).toLocaleDateString("it-IT")}</TableCell>
                  <TableCell className="font-mono text-xs">{s.barcode_full}</TableCell>
                  <TableCell>{wasteLabel(s.waste_type)}</TableCell>
                  <TableCell>{s.verified_weight_kg != null ? `${s.verified_weight_kg} kg` : "—"}</TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-right pr-6"><Link to={`/operator/review/${s.id}`} className="text-primary text-sm hover:underline">Apri →</Link></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Layout>
  );
}
